// Per-viewer projection (slices 04, 07 and 08; docs/VTT/MVP-BUILD-PLAN.md §2.5).
//
// The ONE statement of who sees what. Both functions are WHITELISTS: a
// viewer's payload is built from what they may see, never the full state
// with things removed. The DM sees everything, hidden things flagged (they
// render at half transparency); a player sees only revealed objects, and of
// those only the fields listed here — no layer (players have no layer
// interface, design/11 §3), no owners but their own standing, no undelete.
// An event touching only what the viewer cannot see projects to NOTHING —
// no envelope — so no count, gap or placeholder can announce it
// (design/22-hidden-means-absent.md). A move carries its route, so
// everyone watches the token travel (design/12 §4).
import { copyFloors, hasFloors, playerFloors } from './floors.js';
import { seenInto } from './objects.js';
import { projectToggles } from '../rules/starts.js';
import { chaseFor } from '../rules/vehicleCombat.js';
import { copySenses } from '../sight/vision.js';
import { copyHolding, copyLoadouts, copySpells } from './spells.js';
import { copyForms, copyWearing } from './forms.js';
import { copyAuras } from './auras.js';
import { copyMatrixState, matrixSeen } from '../rules/matrixHost.js';
import { playerMatrixRoll } from '../rules/matrix.js';
import { canSee, destinationOf, dispositionOf, isEncounterBody, isFogBody, isLightBody, isToggleBody, isVisionBody, isWeatherBody, isWallBody, isZoneBody, } from './objects.js';
import { followMoves } from './follow.js';
import { holds, seesAsDm, withholdsThrow } from '../table/codm.js';
import { playerApplied } from '../dice/dice.js';
import { isAskBody, projectAskEvent } from '../dice/asks.js';
import { playerAttack } from '../rules/attack.js';
import { playerTrigger } from '../rules/triggers.js';
import { playerSave } from '../rules/save.js';
import { playerTableFacts } from '../dice/tables.js';
import { forPlayers } from '../rules/inferred.js';
import { isRulingBody, playerRulings } from '../rules/rulings.js';
import { isAudioBody, normaliseAudio } from './audio.js';
import { isCueBody } from './cues.js';
import { isDialogBody, playerDialogLine } from './dialog.js';
import { isCardBody, projectCardEvent } from './cards.js';
import { copyLinks, isLinkBody, playerLinks } from './links.js';
import { audienceHas, isHandoutBody, isPinBody, projectHandoutEvent } from './handouts.js';
import { projectEncounter } from './encounter.js';
/** An area as a viewer gets it: its maker (qa-runs#1451; which token cast it) is the DM's alone, as a hidden caster's id would show. */
function areaFor(a, viewer) {
    if (viewer.role === 'dm' || a.maker === undefined)
        return a;
    const { maker: _maker, ...rest } = a;
    return rest;
}
import { seesDrawing } from './drawings.js';
import { copyWalls } from './walls.js';
import { copyZones, difficultSpans } from './zones.js';
import { isPauseBody, playerPaused } from './triggers.js';
import { intersectSpans, projectFog } from './fog.js';
import { advanceSight, playerLights, playerSight, playerWalls, visionOn } from '../sight/sight.js';
import { copyWeather } from '../sight/weather.js';
import { copyLights } from '../sight/light.js';
import { mapShown, playerFloorTiles, playerTiles } from '../sight/tiles.js';
import { splitNumbered } from './naming.js';
/**
 * The viewer a co-DM's BOARD is projected as (issue 1159): the DM with `see_hidden`, a token-less player — a watcher,
 * issue 1040 — without it. Anyone else is themselves.
 */
export function boardViewer(viewer) {
    if (viewer.role !== 'codm')
        return viewer;
    return { id: viewer.id, role: seesAsDm(viewer) ? 'dm' : 'watcher' };
}
/** The numbers a viewer may see of a token (design/16 §4, design/19 Q4): a whitelist, field by field. */
export function visibleValues(o, viewer) {
    if (viewer.role === 'dm' || o.owners.includes(viewer.id))
        return { ...o.values };
    const out = {};
    for (const key of Object.keys(o.values)) {
        // Shared on the board, or told in Properties (issue 1575): either sends the value; neither, and it is absent.
        // A token's other defences (qa-runs 402) go with its AC: shared or told as it is, and with it. Its levels go with its conditions.
        const field = (key === 'defences' ? 'ac' : key === 'condition_levels' ? 'conditions' : key === 'dying' ? 'hp' : key);
        if (o.shares[field] || o.told?.includes(field))
            out[key] = o.values[key];
    }
    return out;
}
/** A player's weather (issue 1163): with the scene's vision on and a weather set, its key and name; else null. */
export function playerWeather(state) {
    const w = state.weather;
    return w && visionOn(state) ? { key: w.key, label: w.label } : null;
}
/** The pins a player may see (issue 1154): a handout's, sent to them, at a place they can see — design/30 §4's "through canSee". */
export function playerPins(state, viewer) {
    const out = [];
    for (const p of Object.values(state.pins ?? {})) {
        if (!('handout' in p.target) || !audienceHas(p.to, viewer.id))
            continue;
        // Gated like a token standing on that point (one square, nobody's own).
        if (!canSee({ hidden: false, x: p.at.x, y: p.at.y, size: { w: 1, h: 1 }, owners: [], pc: false }, viewer, state))
            continue;
        out.push({ id: p.id, at: { x: p.at.x, y: p.at.y }, label: p.label, handout: p.target.handout });
    }
    return out;
}
const pinRecord = (pins) => Object.fromEntries(pins.map((p) => [p.id, p]));
/**
 * Whether a player who can see a token learns its name (COMBAT-007): their own, or its name share is on. A player
 * character's name always shows: its sharing record governs its numbers between players, not who it is.
 */
/** Whether the DM revealed a token's side to the players (qa-runs#1157): shared on the board or told in Properties. */
export const sideRevealed = (o) => o.shares.side === true || (o.told?.includes('side') ?? false);
export function nameShown(o, mine) {
    return mine || o.pc || o.shares.name;
}
/** What a viewer may see of an object: the whole thing for the DM, a player's slice for a player, nothing for a hidden one. */
const copyInferred = (i) => ({
    defences: Object.fromEntries(Object.entries(i.defences).map(([k, d]) => [k, { ...d }])),
    damage: { dealt: i.damage.dealt, hits: [...i.damage.hits] },
});
/** A token named as its entry, or as its entry numbered (qa-runs#362): "Goblin" and "Goblin 3" for a goblin. */
const namedAsEntry = (name, entry) => !!name && (name === entry || splitNumbered(name).stem === entry.trim());
export function projectObject(o, viewer, scope) {
    if (viewer.role === 'dm')
        return { ...o, owners: [...o.owners], size: { ...o.size }, values: { ...o.values }, shares: { ...o.shares }, ...(o.entry ? { entry: { ...o.entry } } : {}), ...(o.wiki ? { wiki: { ...o.wiki } } : {}), ...(o.facts ? { facts: o.facts.map((f) => ({ ...f })) } : {}), ...(o.told ? { told: [...o.told] } : {}), ...(o.inferred ? { inferred: copyInferred(o.inferred) } : {}), ...(o.vision ? { vision: copySenses(o.vision) } : {}), ...(o.spells ? { spells: copySpells(o.spells) } : {}), ...(o.loadouts ? { loadouts: copyLoadouts(o.loadouts) } : {}), ...(o.auras ? { auras: copyAuras(o.auras) } : {}), ...(o.matrix ? { matrix: copyMatrixState(o.matrix) } : {}), ...(o.forms ? { forms: copyForms(o.forms) } : {}), ...(o.wearing ? { wearing: copyWearing(o.wearing) } : {}) };
    if (!canSee(o, viewer, scope))
        return null;
    const mine = o.owners.includes(viewer.id);
    // A thing the DM is placing stands, for a player, where they last saw it, at the count they saw (issue 811).
    const at = o.held?.shown ?? { x: o.x, y: o.y };
    const slice = {
        id: o.id, kind: o.kind, x: at.x, y: at.y, size: { ...o.size }, fit: o.fit,
        mine, owners: [...o.owners], nameplate: o.nameplate, asset: o.asset, locked: o.locked, pc: o.pc, dead: o.dead, marker: o.marker, values: visibleValues(o, viewer),
    };
    // A name told in Properties (issue 1575) is sent as a shared one is.
    if (nameShown(o, mine) || o.told?.includes('name'))
        slice.name = o.name;
    if (o.told?.length)
        slice.told = [...o.told];
    if (o.floor)
        slice.floor = o.floor;
    if (o.z !== undefined)
        slice.z = o.z;
    if (o.rotation)
        slice.rotation = o.rotation;
    if (o.wiki)
        slice.wiki = true;
    if (o.light)
        slice.light = { ...o.light };
    if (mine)
        slice.rev = o.held ? o.held.rev : o.rev;
    if (mine)
        slice.shares = { ...o.shares };
    if (mine && o.character)
        slice.character = o.character;
    if (mine && o.companion)
        slice.companion = { ...o.companion };
    if (mine && (o.character || o.companion) && o.hpOffSheet)
        slice.hpOffSheet = true;
    // Issue 1500: whether the DM turned off its attacks of opportunity — the owner's to know, nobody else's.
    if (mine && o.noAoo)
        slice.noAoo = true;
    // qa-runs#1157: its side — an owner may read their own token's, as its noAoo; another player only once the DM has
    // revealed it (shared on the board or told in Properties), and then as it reads now, its default included.
    if (mine && o.disposition)
        slice.disposition = o.disposition;
    else if (!mine && sideRevealed(o))
        slice.disposition = dispositionOf(o);
    // Issue 1531: the astral state it is in — its owners' and the DM's to know, never another player's.
    if (mine && o.astral)
        slice.astral = o.astral;
    if (mine && o.jacked)
        slice.jacked = true;
    // qa-runs#1173: a decker's owners read the IC they have met (never the host, the tally or an unmet IC).
    const seenM = mine && o.matrix ? matrixSeen(o.matrix) : null;
    if (seenM)
        slice.matrixSeen = seenM;
    // Issue 1130: the action bar's spells and loadouts are the owner's working notes — another player is sent neither key.
    if (mine && o.vision?.length)
        slice.vision = copySenses(o.vision);
    if (mine && o.spells?.length)
        slice.spells = copySpells(o.spells);
    if (mine && o.concentrating) {
        const { roll: _roll, ...held } = copyHolding(o.concentrating);
        slice.concentrating = held;
    }
    if (mine && o.loadouts)
        slice.loadouts = copyLoadouts(o.loadouts);
    // qa-runs#1441: its forms and the true form it changes back to — the owners' to switch; another player sees only how it looks now.
    if (mine && o.forms?.length)
        slice.forms = copyForms(o.forms);
    if (mine && o.wearing)
        slice.wearing = copyWearing(o.wearing);
    if (o.auras?.length)
        slice.auras = mine ? copyAuras(o.auras) : o.auras.map(({ label, mods: _m, ...a }) => ({ ...a, ...(o.shares.conditions ? { label } : {}), mods: [] }));
    // Hidden means absent (issue 1031): the leader's id only while the leader itself is in this player's sight.
    const leader = mine && o.follows ? scope?.objects?.[o.follows] : undefined;
    if (leader && o.follows && canSee(leader, viewer, scope)) {
        slice.follows = o.follows;
        if (o.mounted)
            slice.mounted = true;
    }
    // Issue 1045: a creature the DM named otherwise than its entry ("Creature 1" for a goblin) keeps the entry's name
    // from a player: its own name is what they were meant to learn. Its owner still holds it, as they hold its numbers.
    // Issue 1585: under that same condition, its address too (never its system, uuid or book). qa-runs#362: the entry's
    // name numbered ("Goblin 3", as every placed creature is) is still the entry's own name.
    if (o.entry && nameShown(o, mine) && (mine || namedAsEntry(o.name, o.entry.name)))
        slice.entry = { name: o.entry.name, segment: o.entry.segment, slug: o.entry.slug };
    // Issue 1057: what the DM revealed, deliberately, of a thing they can see — whatever its name share says.
    if (o.facts?.length)
        slice.facts = o.facts.map((f) => ({ label: f.label, value: f.value }));
    // Issue 1133: what the attacks they were shown say of it, only once the DM has turned it on for this creature.
    if (o.infer) {
        slice.infer = true;
        if (o.inferred)
            slice.inferred = forPlayers(o.inferred, o.facts);
    }
    return slice;
}
export function projectState(state, viewer) {
    // A co-DM (issue 1159): the DM's board or a watcher's, by `see_hidden`; the DM's pins (a note's words, an unsent
    // handout's place) are `notes`'s, so on the DM's board without it there is no pins key at all.
    if (viewer.role === 'codm') {
        const board = projectState(state, boardViewer(viewer));
        // Scene links (issue 1173) name the table's scenes, which are `map`'s: without it, a DM's board carries no links key.
        let out = board;
        if (seesAsDm(viewer) && !holds(viewer, 'notes')) {
            const { pins: _p, ...rest } = out;
            out = rest;
        }
        if (seesAsDm(viewer) && !holds(viewer, 'map')) {
            const { links: _l, ...rest } = out;
            out = rest;
        }
        return out;
    }
    const objects = {};
    for (const o of Object.values(state.objects)) {
        const p = projectObject(o, viewer, state);
        if (p)
            objects[o.id] = p;
    }
    const base = { objects, grid: state.grid, scene: sceneFor(state, viewer), encounter: projectEncounter(state, viewer), focuses: { ...state.focuses }, areas: Object.fromEntries(Object.entries(state.areas).map(([k, a]) => [k, areaFor(a, viewer)])), drawings: (state.drawings ?? []).filter((d) => seesDrawing(d, viewer)).map((d) => ({ ...d, points: [...d.points] })), fog: viewer.role === 'dm' ? projectFog(state.fog, 'dm') : playerFogOf(state, viewer) };
    // Walls (issue 1144): the DM's whole, with the scene's vision. A player is sent design/30 §4's walls while vision is
    // on, and while it is off the doors alone (qa-runs#1202): no plain wall, and NO key with no door — hidden means absent.
    const dmLinks = copyLinks(state.links);
    const links = playerLinks(state.links);
    const seesToken = (id) => { const o = state.objects[id]; return !!o && canSee(o, viewer, state); };
    const chase = state.chase ? (viewer.role === 'dm' ? state.chase : chaseFor(state.chase, seesToken)) : null;
    if (chase)
        base.chase = chase;
    // Toggles (qa-runs#371): the DM's all; a player's on the tokens projected for them only.
    const toggles = projectToggles(state.toggles, viewer.role, (id) => !!objects[id]);
    if (toggles)
        base.toggles = toggles;
    const paused = pausedFor(state, viewer);
    if (paused.length)
        base.paused = paused;
    if (viewer.role === 'dm')
        return { ...base, ...(Object.keys(dmLinks).length ? { links: dmLinks } : {}), pins: Object.fromEntries(Object.values(state.pins ?? {}).map((p) => [p.id, { ...p, at: { ...p.at }, target: { ...p.target }, to: Array.isArray(p.to) ? [...p.to] : p.to }])), undelete: state.undelete, placing: state.placing, walls: copyWalls(state.walls ?? []), vision: { ...(state.vision ?? { on: false, sight: 'party', extent: null }) }, lightLevel: state.lightLevel ?? 'bright', lights: copyLights(state.lights ?? []), weather: copyWeather(state.weather), zones: copyZones(state.zones ?? []), ...(state.waiting?.length ? { waiting: state.waiting.map((w) => ({ ...w })) } : {}) };
    // Light (issue 1145): the placed lights whose source a player sees, with vision on in a dim or dark scene; else no key.
    const walls = playerWalls(state, viewer.id);
    const lights = playerLights(state, viewer.id);
    const pins = playerPins(state, viewer);
    const weather = playerWeather(state);
    return { ...base, ...(walls ? { walls } : {}), ...(lights ? { lights } : {}), ...(weather ? { weather } : {}), ...(pins.length ? { pins: pinRecord(pins) } : {}), ...(links.length ? { links: linkRecord(links) } : {}) };
}
/**
 * The scene's settings as a viewer may know them (qa-runs#1160; design/32 §4): the DM's whole; a player's floors only
 * the floors their tokens are on, each with where it starts — no other floor's name, height or place in the order.
 */
export function sceneFor(state, viewer) {
    // The default token spot (qa-runs#1425) goes only to those who may set it: a player is sent nothing of it.
    const { floors, tokenSpot, ...others } = state.scene;
    const rest = tokenSpot && holds(viewer, 'map') ? { ...others, tokenSpot: { ...tokenSpot } } : others;
    if (!floors)
        return { ...rest };
    if (viewer.role === 'dm')
        return { ...rest, floors: copyFloors(floors) };
    const mine = playerFloors(floors, state.objects, viewer.id, seenInto(state, viewer.id));
    return mine?.length ? { ...rest, floors: mine } : { ...rest };
}
const linkRecord = (links) => Object.fromEntries(links.map((l) => [l.id, l]));
/**
 * The fog as a player may know it (issue 851), and with the scene's vision on (issue 1144; design/30 §4) the squares
 * their sight groups have explored and those they see now — nothing of anyone else's.
 */
export function playerFogOf(state, viewer) {
    const fog = projectFog(state.fog, 'player');
    const sight = playerSight(state, viewer.id);
    const tiles = playerTiles(state, viewer.id);
    const tilesOn = playerFloorTiles(state, viewer.id);
    // Difficult terrain (issue 1151): its squares in the map they hold, so no unexplored square's terrain reaches them.
    const all = difficultSpans(state.zones ?? []);
    const shown = all.length ? mapShown(state, viewer.id) : null;
    const difficult = shown ? intersectSpans(all, shown) : all;
    return { ...fog, ...(sight ? { explored: sight.explored, ...(sight.exploredOn ? { exploredOn: sight.exploredOn } : {}), seen: sight.seen, ...(sight.dim ? { dim: sight.dim } : {}), ...(sight.grey ? { grey: sight.grey } : {}) } : {}), ...(tiles ? { tiles } : {}), ...(tilesOn ? { tilesOn } : {}), ...(difficult.length ? { difficult } : {}) };
}
/** The held tokens a viewer is sent (qa-runs#1426): the DM's all; anyone else's only their own tokens they see. */
function pausedFor(state, viewer) {
    if (viewer.role === 'dm')
        return [...(state.paused ?? [])];
    return playerPaused(state.paused, (id) => { const o = state.objects[id]; return !!o && o.owners.includes(viewer.id) && canSee(o, viewer, state); });
}
/** A player's frame with their held tokens riding on it when those changed, or alone. */
function withPaused(ev, before, after, viewer) {
    if (!before.paused?.length && !after.paused?.length)
        return ev;
    const was = pausedFor(before, viewer);
    const now = pausedFor(after, viewer);
    if (JSON.stringify(was) === JSON.stringify(now))
        return ev;
    return ev ? { ...ev, paused: now } : { kind: 'move.paused', paused: now };
}
export function projectEvent(body, before, after, viewer, actorId, at, actorRole) {
    // A rewind (issue 1169) is everyone's fresh projection of the restored scene, the DM's included: never the stamp, and
    // never a delta worked out against what they held — the restored state is a different past, not a step from now.
    if (body.kind === 'scene.rewind')
        return { kind: 'table.rewind', state: projectState(after, viewer), ...(body.undo ? { undo: true } : {}) };
    // Cards (issue 1177): the same rule for every viewer, the DM's included — their own hand whole, others' counts, never
    // the draw pile's order — so the stamped event (the shuffled order, the ids drawn) reaches nobody, not even the DM.
    if (isCardBody(body))
        return projectCardEvent(body, before.decks, after.decks, viewer.id);
    if (viewer.role === 'dm')
        return { kind: 'dm.event', body, actorId, at, ...(actorRole ? { role: actorRole } : {}) };
    if (viewer.role === 'codm')
        return projectCodmEvent(body, before, after, viewer, actorId, at, actorRole);
    // The table's sound (issue 1161): the same for everyone at the table, sent whole. It names only what is in play.
    if (isAudioBody(body))
        return { kind: 'audio', audio: normaliseAudio(after.audio) };
    // NPC dialog (qa-runs#1167): only to the players the server stamped as hearing it, each as they heard it.
    if (isDialogBody(body))
        return dialogFrame(body, viewer.id, at);
    // A sound cue (issue 1162) is the DM's alone: what it fires reaches the table as the sound above, the cue never.
    if (isCueBody(body))
        return null;
    // Floors (qa-runs#1160; design/32 §4): when the floors a player has change, everything they hold may, so they are sent
    // their whole projection afresh — nothing of a floor they left lingers, nothing of one they never had arrives as a delta.
    if ((hasFloors(before.scene) || hasFloors(after.scene)) && floorsChanged(before, after, viewer)) {
        advanceSight(before, after);
        return { kind: 'table.floors', state: projectState(after, viewer) };
    }
    // Sight is worked out from the state before, for the eyes this change affects (design/30 §2.1).
    advanceSight(before, after);
    const ev0 = withFollowChanges(projectPlayerEvent(body, before, after, viewer, at, actorId), before, after, viewer);
    const ev = withChase(withPins(visionOn(before) || visionOn(after) || doorsChanged(before, after, viewer) ? withSight(ev0, before, after, viewer) : ev0, before, after, viewer), body, before, after, viewer);
    const encBefore = projectEncounter(before, viewer);
    const encAfter = projectEncounter(after, viewer);
    if (JSON.stringify(encBefore) === JSON.stringify(encAfter))
        return withPaused(ev, before, after, viewer);
    if (!ev)
        return withPaused({ kind: 'encounter.change', encounter: encAfter }, before, after, viewer);
    if (ev.kind === 'object.appear' || ev.kind === 'object.change' || ev.kind === 'object.vanish' || ev.kind === 'objects.appear' || ev.kind === 'objects.change' || ev.kind === 'objects.vanish' || ev.kind === 'fog.change' || ev.kind === 'grid.change')
        return withPaused({ ...ev, encounter: encAfter }, before, after, viewer);
    return withPaused(ev, before, after, viewer);
}
function floorsChanged(before, after, viewer) {
    return JSON.stringify(playerFloors(before.scene.floors, before.objects, viewer.id, hasFloors(before.scene) ? seenInto(before, viewer.id) : undefined) ?? null) !== JSON.stringify(playerFloors(after.scene.floors, after.objects, viewer.id, hasFloors(after.scene) ? seenInto(after, viewer.id) : undefined) ?? null);
}
/**
 * A co-DM's frame (issue 1159; decision D on #1062): the board's part by `see_hidden` — the DM's event whole, or a
 * watcher's frame — and the parts `notes` and `dice` cover by those powers whichever board they see. Without the
 * power, a part reaches them as it reaches a watcher (a handout sent to everyone, a public roll, the rulings as a player
 * reads them), or not at all. Their OWN private throw and its fudge are theirs whole.
 */
function projectCodmEvent(body, before, after, viewer, actorId, at, actorRole) {
    const asDm = seesAsDm(viewer);
    const whole = { kind: 'dm.event', body, actorId, at, ...(actorRole ? { role: actorRole } : {}) };
    /** As a watcher would receive it: the player's shape, for the log beside the state on either board. */
    // A trigger's chain read as a watcher reads it still names its rule (Tom's C on #1500: the DM and co-DMs see the name).
    const asWatcher = () => {
        const ev = projectEvent(body, before, after, { id: viewer.id, role: 'watcher' }, actorId, at);
        const record = body.kind === 'roll' ? body.roll : body.kind === 'attack.answer' || body.kind === 'save.rethrow' ? body.record : undefined;
        if (ev?.kind !== 'trigger' || !record)
            return ev;
        const t = playerTrigger(record, viewer.id, { names: true });
        return t ? { kind: 'trigger', trigger: t } : null;
    };
    const board = () => (asDm ? whole : asWatcher());
    // notes: handouts (an unsent one's words) and pins (a note pin's words).
    if (isHandoutBody(body))
        return holds(viewer, 'notes') ? whole : projectHandoutEvent(body, before.handouts ?? {}, after.handouts ?? {}, viewer.id);
    if (isPinBody(body))
        return asDm ? (holds(viewer, 'notes') ? whole : null) : asWatcher();
    // Scene links (issue 1173) name the table's scenes: the DM's event with `map` on the DM's board (none without it), else as a watcher sees them.
    if (isLinkBody(body))
        return asDm ? (holds(viewer, 'map') ? whole : null) : asWatcher();
    // The table's sound (issue 1161): public to the table; the DM's event with `notes` (they drive the playlist), else as a watcher hears it.
    if (isAudioBody(body))
        return holds(viewer, 'notes') ? whole : asWatcher();
    // NPC dialog (qa-runs#1167): whole on the DM's board (who heard it, the real name); off it, nothing — a co-DM is no listener.
    if (isDialogBody(body))
        return asDm ? whole : null;
    // A sound cue (issue 1162) names a zone or a token: the DM's with `notes`, on the DM's board alone.
    if (isCueBody(body))
        return asDm && holds(viewer, 'notes') ? whole : null;
    // dice: the DM's private throws, the fudge, requests, rulings and the table's rules — whole with the power on the
    // DM's board; their own private throw and its fudge whole always; anything else as a watcher reads it.
    const dice = holds(viewer, 'dice') && asDm;
    // Whether a throw reached them whole: a public one on the DM's board; a private one, or one made for someone, with
    // dice, or their own — but a template's saves or an attack names its creatures, so off the DM's board their own is
    // read as a watcher reads it (a co-DM without see_hidden resolving a template over a hidden monster; found by the
    // fuzz, issue 1160). A guest DM handed the session without hidden rolls (issue 1160): never the main DM's private throw.
    const throwWhole = (r, by) => {
        if (!(r.private || r.by))
            return asDm;
        if (withholdsThrow(viewer, by, r))
            return false;
        return dice || (r.by === null && r.actor.id === viewer.id && (asDm || (!r.save && !r.attack)));
    };
    if (body.kind === 'roll' && (body.roll.private || body.roll.by))
        return throwWhole(body.roll, actorId) ? whole : asWatcher();
    // Settling a throw's damage carries the throw's record whole: it reaches them exactly as the throw did (the review
    // of 2026-10-10: a co-DM with see_hidden but not dice was sent the DM's private attack whole through its Apply).
    if ((body.kind === 'attack.apply' || body.kind === 'save.apply' || body.kind === 'roll.apply' || body.kind === 'roll.settle') && body.record)
        return throwWhole(body.record, null) ? whole : asWatcher();
    if (body.kind === 'roll.amend' || body.kind === 'attack.ac' || body.kind === 'attack.answer' || body.kind === 'save.bonus' || body.kind === 'save.rethrow' || body.kind === 'test.karma') {
        // The main DM's fudge is a hidden roll too, and so is any change that carries a withheld throw (issue 1160).
        if ((body.kind === 'roll.amend' && withholdsThrow(viewer, actorId)) || (body.record && withholdsThrow(viewer, null, body.record)))
            return asWatcher();
        if (dice || (body.kind === 'roll.amend' && actorId === viewer.id))
            return whole;
        // A defence or a bonus follows its throw: a public one reached them whole on the DM's board, so its change does,
        // or the browser, holding the whole roll, adds the player's copy beside it (the review of 2026-10-18).
        if ((body.kind === 'attack.ac' || body.kind === 'attack.answer' || body.kind === 'save.bonus' || body.kind === 'save.rethrow' || body.kind === 'test.karma') && body.record && throwWhole(body.record, null))
            return whole;
        return asWatcher();
    }
    // A rule-check line names the combatant, hidden or not: `dice` on the DM's board, as the log page reads it (the
    // review of 2026-10-10: by `dice` alone it told a co-DM without see_hidden a hidden creature's name).
    if (body.kind === 'log.note' && body.rule)
        return dice ? whole : asWatcher();
    if (isRulingBody(body) || body.kind === 'table.rules' || isAskBody(body))
        return holds(viewer, 'dice') ? whole : asWatcher();
    return board();
}
/** A change to what pins this player sees (issue 1154) — a pin set or gone, its handout's audience, sight moving — rides along. */
function withPins(ev, before, after, viewer) {
    if (!Object.keys(before.pins ?? {}).length && !Object.keys(after.pins ?? {}).length)
        return ev;
    const was = playerPins(before, viewer);
    const now = playerPins(after, viewer);
    if (JSON.stringify(was) === JSON.stringify(now))
        return ev;
    return ev ? { ...ev, pins: now } : { kind: 'pins', pins: now };
}
/**
 * The chase as a player reads it (qa-runs#1174) changes with what they see: a vehicle in it hidden, shown, removed or
 * brought back changes their copy though the event never names the chase. The new copy rides along, or alone as
 * `chase.set`, so the browser's reduction equals a fresh projection.
 */
function withChase(ev, body, before, after, viewer) {
    if (body.kind === 'chase.set' || (!before.chase && !after.chase))
        return ev;
    const sees = (s) => (id) => { const o = s.objects[id]; return !!o && canSee(o, viewer, s); };
    const was = chaseFor(before.chase, sees(before));
    const now = chaseFor(after.chase, sees(after));
    if (JSON.stringify(was) === JSON.stringify(now))
        return ev;
    return ev ? { ...ev, chase: now } : { kind: 'chase.set', chase: now };
}
/**
 * A follower's link, as a player reads it, changes with its LEADER's sight (issue 1031): the leader hidden, removed,
 * brought back or given away, the follower's `follows` goes or comes though the event names only the leader. Those
 * followers ride along, whole, in one `objects.change`, so the browser's reduction equals a fresh projection.
 */
function withFollowChanges(ev, before, after, viewer) {
    const ids = new Set();
    for (const o of [...Object.values(before.objects), ...Object.values(after.objects)])
        if (o.follows && o.owners.includes(viewer.id))
            ids.add(o.id);
    if (!ids.size)
        return ev;
    const told = new Set();
    if (ev && (ev.kind === 'object.appear' || ev.kind === 'object.change'))
        told.add(ev.object.id);
    if (ev && (ev.kind === 'objects.appear' || ev.kind === 'objects.change'))
        for (const o of ev.objects)
            told.add(o.id);
    if (ev && ev.kind === 'object.vanish')
        told.add(ev.id);
    if (ev && ev.kind === 'objects.vanish')
        for (const id of ev.ids)
            told.add(id);
    if (ev && 'appear' in ev && ev.appear)
        for (const o of ev.appear)
            told.add(o.id);
    if (ev && 'vanish' in ev && ev.vanish)
        for (const id of ev.vanish)
            told.add(id);
    const extra = [];
    for (const id of ids) {
        if (told.has(id) || !before.objects[id] || !after.objects[id])
            continue;
        const saw = projectObject(before.objects[id], viewer, before);
        const sees = projectObject(after.objects[id], viewer, after);
        if (saw && sees && JSON.stringify(saw) !== JSON.stringify(sees))
            extra.push(sees);
    }
    if (!extra.length)
        return ev;
    if (!ev)
        return { kind: 'objects.change', objects: extra };
    switch (ev.kind) {
        case 'objects.change': return { ...ev, objects: [...ev.objects, ...extra] };
        case 'object.change': return { kind: 'objects.change', objects: [ev.object, ...extra], ...(ev.route ? { routes: { [ev.object.id]: ev.route } } : {}) };
        case 'object.appear': return { kind: 'objects.change', objects: extra, appear: [ev.object] };
        case 'objects.appear': return { kind: 'objects.change', objects: extra, appear: ev.objects };
        case 'object.vanish': return { kind: 'objects.change', objects: extra, vanish: [ev.id] };
        case 'objects.vanish': return { kind: 'objects.change', objects: extra, vanish: ev.ids };
        default: return ev; // the grid, the fog, the scene, the log: none moves a player's own leader out of their sight
    }
}
function projectPlayerEvent(body, before, after, viewer, at, actorId) {
    if (isEncounterBody(body))
        return null; // the order's own events carry nothing but the reading, attached above
    // The chase (issue 1174): only the vehicles this player sees, before and after; nothing to one who sees none either way.
    if (body.kind === 'chase.set') {
        const sees = (s) => (id) => { const o = s.objects[id]; return !!o && canSee(o, viewer, s); };
        const was = chaseFor(before.chase, sees(before));
        const now = chaseFor(after.chase, sees(after));
        return was || now ? { kind: 'chase.set', chase: now } : null;
    }
    // Walls and doors (issue 1144) are the DM's: a player is sent no frame of one, only what it changes of their own walls
    // through `withSight` (design/30 §4; with vision off, the doors they are sent, qa-runs#1202).
    if (isWallBody(body))
        return null;
    // The vision switch and setting (issue 1144, slice 2) are the DM's: what they change of a player's sight reaches them
    // through `withSight`, as a fog change, never the event.
    if (isVisionBody(body))
        return null;
    // Movement paused (qa-runs#1426): a player learns only their own held tokens, through `withPaused`; the trigger never.
    if (isPauseBody(body))
        return null;
    // The light level and placed lights (issue 1145) are the DM's: a player learns what they change of their sight through
    // `withSight` — the squares now seen and dim, and a light whose source they see — never the event.
    if (isLightBody(body))
        return null;
    // The weather (issue 1163) is the DM's event: a player is sent its name and what it does to their sight through
    // `withSight`, with vision on only — with vision off, nothing.
    if (isWeatherBody(body))
        return null;
    // A toggle (qa-runs#371) is as visible as its token: a player who sees it is sent their toggles whole; else nothing.
    if (isToggleBody(body)) {
        const sees = (id) => { const o = after.objects[id]; return !!o && canSee(o, viewer, after); };
        if (!sees(body.token))
            return null;
        return { kind: 'toggles', toggles: projectToggles(after.toggles, viewer.role, sees) ?? {} };
    }
    // Vision zones (issue 1149) are the DM's: a player is sent nothing of a zone as such (design/30 §4 names none), only
    // what it does to their sight, through `withSight` — and difficult terrain's squares in their map (issue 1151), in
    // their fog, which a zone event changes with vision off too.
    if (isZoneBody(body)) {
        const fog = playerFogOf(after, viewer);
        return JSON.stringify(fog.difficult ?? null) !== JSON.stringify(playerFogOf(before, viewer).difficult ?? null) ? { kind: 'fog.change', fog } : null;
    }
    // Area templates (issue 832) are shared facts too.
    if (body.kind === 'area.set')
        return { kind: 'area.set', area: areaFor(after.areas[body.area.id] ?? body.area, viewer) };
    if (body.kind === 'area.clear')
        return before.areas[body.id] ? { kind: 'area.clear', ids: [body.id] } : null;
    if (body.kind === 'area.clearAll') {
        const ids = Object.keys(before.areas);
        return ids.length ? { kind: 'area.clear', ids } : null;
    }
    // Drawings (issue 1032) are shared facts of the scene too, whole.
    // The DM's planning drawings (issue 1033) are the DM's alone: a player is sent no frame of one, made or erased.
    if (body.kind === 'draw.add') {
        const id = body.drawing.id;
        const d = after.drawings.find((x) => x.id === id);
        return d && seesDrawing(d, viewer) && !before.drawings.some((x) => x.id === d.id) ? { kind: 'draw.add', drawing: { ...d, points: [...d.points] } } : null;
    }
    if (body.kind === 'draw.addMany') {
        const drawings = body.drawings
            .filter((x) => !before.drawings.some((y) => y.id === x.id))
            .map((x) => after.drawings.find((y) => y.id === x.id))
            .filter((d) => d !== undefined && seesDrawing(d, viewer))
            .map((d) => ({ ...d, points: [...d.points] }));
        return drawings.length ? { kind: 'draw.addMany', drawings } : null;
    }
    if (body.kind === 'draw.erase' || body.kind === 'draw.eraseMine') {
        const ids = before.drawings.filter((d) => seesDrawing(d, viewer) && !after.drawings.some((x) => x.id === d.id)).map((d) => d.id);
        return ids.length ? { kind: 'draw.erase', ids } : null;
    }
    // Focuses (slice 13) are shared facts: every viewer gets the same thing.
    if (body.kind === 'focus.set')
        return { kind: 'focus.set', focus: after.focuses[body.focus.id] ?? body.focus, at };
    if (body.kind === 'focus.clear')
        return before.focuses[body.id] ? { kind: 'focus.clear', ids: [body.id] } : null;
    if (body.kind === 'focus.clearAll') {
        const ids = Object.keys(before.focuses);
        return ids.length ? { kind: 'focus.clear', ids } : null;
    }
    // The grid and the scene are everyone's: the finished result, once, on release (design/21 §3).
    if (body.kind === 'grid.set') {
        // With the fog on the squares move under the grid (issue 851): what that brings into sight, or takes out of it, rides along.
        const ev = { kind: 'grid.change', grid: after.grid };
        if (after.fog.rev !== before.fog.rev)
            ev.fog = playerFogOf(after, viewer);
        return { ...ev, ...sightDelta(before, after, viewer) };
    }
    // Fog (issue 851): the fog itself changed, so it is always sent; with it, whole, what came into sight and the ids of what left it.
    if (isFogBody(body))
        return { kind: 'fog.change', fog: playerFogOf(after, viewer), ...sightDelta(before, after, viewer) };
    if (body.kind === 'scene.set')
        return { kind: 'scene.change', scene: sceneFor(after, viewer) };
    // The log (design/15 §7, design/22 §4): a private roll, or one rolled for a player, is invisible — no placeholder, no count; an amendment is the DM's alone.
    // An answer to a private group request (issue 1049) reaches the DM and its roller alone: the roller's own, never one rolled for them.
    // An attack (issue 1124; design/29 §6): the player's own version, worked out here from the stored record — never
    // the record. A fudge or a defence the DM supplies sends the new version of the same attack, by its id.
    // A trigger's chain (issue 1500): a public one as this player may know it, again as it carries on; one that names a
    // creature some player cannot see reaches no player at all, nor does its damage settled.
    if (body.kind === 'roll' && body.roll.trigger) {
        const t = playerTrigger(body.roll, viewer.id);
        return t ? { kind: 'trigger', trigger: t } : null;
    }
    if ((body.kind === 'attack.answer' || body.kind === 'save.rethrow') && body.record?.trigger) {
        const t = playerTrigger(body.record, viewer.id);
        return t ? { kind: 'trigger', trigger: t } : null;
    }
    if (body.kind === 'roll' && body.roll.attack) {
        const a = playerAttack(body.roll, viewer.id);
        return a ? { kind: 'attack', attack: a } : null;
    }
    if ((body.kind === 'roll.amend' || body.kind === 'attack.ac' || body.kind === 'attack.answer') && body.record) {
        const a = playerAttack(body.record, viewer.id);
        return a ? { kind: 'attack', attack: a } : null;
    }
    if (body.kind === 'attack.ac' || body.kind === 'attack.answer')
        return null;
    // Damage settled, and the table's rules, are the DM's (issue 1125): a player sees damage only as the hit points they
    // are shown moving, through the token's own change (`object.damage`, below, read like any change to a thing).
    // A full attack's swing set aside (qa-runs#1158) is its attacker's to send on: the line again, as this player may know it.
    if (body.kind === 'attack.apply' && body.leftover && body.record) {
        const a = playerAttack(body.record, viewer.id);
        return a?.leftover ? { kind: 'attack', attack: a } : null;
    }
    if (body.kind === 'attack.apply' || body.kind === 'table.rules')
        return null;
    // The DM's rulings (issue 1136) are everyone's to read: the list as it now stands, in the words the DM wrote.
    if (isRulingBody(body))
        return { kind: 'rulings', rulings: playerRulings(after.rulings ?? []) };
    // A template's saves (issue 1129): the player's own version, from the stored record; a bonus the DM supplies sends the
    // new version by its id. Damage settled is the DM's, seen only as the hit points that move.
    if (body.kind === 'roll' && body.roll.save) {
        const sv = playerSave(body.roll, viewer.id);
        return sv ? { kind: 'save', save: sv } : null;
    }
    if (body.kind === 'save.bonus' || body.kind === 'save.rethrow') {
        const sv = body.record ? playerSave(body.record, viewer.id) : null;
        return sv ? { kind: 'save', save: sv } : null;
    }
    if (body.kind === 'save.apply')
        return null;
    // A roll applied to tokens (qa-runs#406): the roll again, by its id, with where it has been applied — to anyone who saw
    // the roll; an application to a token the DM had hidden is left out.
    if (body.kind === 'roll.apply' || body.kind === 'roll.settle') {
        const r = body.record;
        if (!r)
            return null;
        const own = r.by === null && r.actor.id === viewer.id;
        if ((r.private || r.by) && !own)
            return null;
        return { kind: 'roll', roll: playerRollCopy(r) };
    }
    // A Karma use on a test (issue 1529) follows the test's own visibility: a public test's new version, whole, by its id;
    // a private one's, or one rolled for someone, reaches no player.
    if (body.kind === 'test.karma') {
        const r = body.record;
        if (!r || r.private || r.by)
            return null;
        const { band: _band, ...seen } = r;
        return { kind: 'roll', roll: { ...seen, dice: [...r.dice], fudged: null } };
    }
    // A System Test (issue 1532): a public one as a player may know it — their dice and whether it succeeded, the host's
    // numbers only when the DM showed them, never the tally; a private one reaches no player.
    if (body.kind === 'roll' && body.roll.test?.matrix) {
        if (body.roll.private || body.roll.by)
            return null;
        return { kind: 'roll', roll: playerMatrixRoll(body.roll) };
    }
    if (body.kind === 'roll') {
        // One's own unaided throw reaches one's own sockets, as the log page reads it (qa-runs 1198): an answer to a private
        // request, or the server's line of a player's own initiative roll — never one the DM rolled for them.
        const own = body.roll.by === null && body.roll.actor.id === viewer.id;
        if ((body.roll.private || body.roll.by) && !own)
            return null;
        // A fudge band (issue 1038) rides only on the DM's private throws, which reach no player; stripped all the same.
        const { band: _band, ...seen } = body.roll;
        // A table's outcome (issue 1158) reaches a player as its line: never the creature the DM may place from it.
        return { kind: 'roll', roll: { ...seen, dice: [...body.roll.dice], fudged: null, ...(body.roll.table ? { table: playerTableFacts(body.roll.table) } : {}) } };
    }
    if (isAskBody(body))
        return projectAskEvent(body, before.asks ?? {}, viewer.id);
    // A handout (issue 1154): whole once it is theirs, again when edited while theirs, its removal when taken back.
    if (isCardBody(body))
        return null; // `projectEvent` answers every card event first
    if (isHandoutBody(body))
        return projectHandoutEvent(body, before.handouts ?? {}, after.handouts ?? {}, viewer.id);
    // A pin reaches a player only as their list of pins, through `withPins`: never the event, never a note.
    if (isPinBody(body))
        return null;
    // A scene link (issue 1173) reaches a player as the shown links of this scene, whole, and only when that list changed.
    if (isLinkBody(body)) {
        const was = playerLinks(before.links);
        const now = playerLinks(after.links);
        return JSON.stringify(was) === JSON.stringify(now) ? null : { kind: 'links', links: now };
    }
    if (isAudioBody(body))
        return { kind: 'audio', audio: normaliseAudio(after.audio) };
    if (isDialogBody(body))
        return dialogFrame(body, viewer.id, at);
    if (isCueBody(body))
        return null;
    if (body.kind === 'roll.amend')
        return null;
    if (body.kind === 'undelete.drop')
        return null; // the undelete is the DM's alone (issue 1020): nothing, not even an envelope
    if (body.kind === 'log.clear')
        return { kind: 'log.clear', at, actorId };
    // A rule-check line (issue 1131) is the DM's alone: a player is sent nothing, not even an envelope.
    if (body.kind === 'log.note')
        return body.rule ? null : { kind: 'log.note', note: body.note, ownerId: body.ownerId, at, ...(body.announce ? { announce: true } : {}) };
    if (body.kind === 'placement.set') {
        // Done placing (issue 811): everything placed lands at once — the moved things at their squares with no route,
        // the added ones appearing. Starting to place changes nothing a player sees, so it sends nothing.
        const landed = Object.values(after.objects).map((o) => projectObject(o, viewer, after)).filter((p) => p !== null)
            .filter((p) => { const was = before.objects[p.id]; return JSON.stringify(was ? projectObject(was, viewer, before) : null) !== JSON.stringify(p); });
        // A thing placed into the fog (issue 851) leaves the player's sight as it lands.
        const { vanish } = sightDelta(before, after, viewer);
        if (!landed.length && !vanish)
            return null;
        return vanish ? { kind: 'objects.change', objects: landed, vanish } : { kind: 'objects.change', objects: landed };
    }
    // A familiar follows its owner (issue 1031): a move that carried followers reads as the group move it was.
    if (body.kind === 'object.move' || body.kind === 'objects.move') {
        const led = body.kind === 'object.move' ? [{ id: body.id, route: body.route, rev: body.rev }] : body.moves;
        const followers = followMoves(before, led);
        if (followers.length)
            body = { kind: 'objects.move', moves: [...led, ...followers] };
    }
    if (body.kind === 'objects.move') {
        // A group move (issue 830): what this player sees of each thing that visibly moved, and its route — one frame for all.
        // A hidden thing, or one the DM is placing, projects to nothing, so it is absent here, route and all.
        // Into the fog, a thing leaves their sight (its id in `vanish`, no route); out of it, it appears where it stands (issue 851).
        const objects = [];
        const routes = {};
        const appear = [];
        const vanish = [];
        for (const m of body.moves) {
            const was = before.objects[m.id];
            const now = after.objects[m.id];
            const saw = was ? projectObject(was, viewer, before) : null;
            const sees = now ? projectObject(now, viewer, after) : null;
            if (saw && !sees) {
                vanish.push(m.id);
                continue;
            }
            if (!saw && sees) {
                appear.push(sees);
                continue;
            }
            if (!saw || !sees || JSON.stringify(saw) === JSON.stringify(sees))
                continue;
            objects.push(sees);
            routes[m.id] = sightRoute(m.route, now, viewer, after);
        }
        if (!objects.length && !appear.length && !vanish.length)
            return null;
        const ev = { kind: 'objects.change', objects, routes };
        if (appear.length)
            ev.appear = appear;
        if (vanish.length)
            ev.vanish = vanish;
        return ev;
    }
    if (body.kind === 'scene.rewind')
        return null; // answered above, whole, for every viewer
    if (body.kind === 'owners.prune') {
        // What a player sees of each thing whose owners changed; nothing when they see none of them.
        const changed = Object.keys(after.objects).filter((id) => before.objects[id] && before.objects[id].owners.length !== after.objects[id].owners.length)
            .map((id) => projectObject(after.objects[id], viewer, after)).filter((p) => p !== null);
        // A departed author's drawings now belong to nobody (issue 1032): whole, as everyone holds them.
        const drawings = after.drawings.filter((d) => seesDrawing(d, viewer) && before.drawings.find((x) => x.id === d.id)?.ownerId !== d.ownerId);
        if (!changed.length && !drawings.length)
            return null;
        return drawings.length ? { kind: 'objects.change', objects: changed, drawings } : { kind: 'objects.change', objects: changed };
    }
    if (body.kind === 'object.remove' || body.kind === 'object.restore' || body.kind === 'object.duplicate' || body.kind === 'objects.paste' || body.kind === 'object.reveal' || body.kind === 'object.hide') {
        // Several things at once: what vanished that they saw; what appeared that they may see — an ambush revealed lands together.
        const saw = (id) => (before.objects[id] ? projectObject(before.objects[id], viewer, before) : null);
        const sees = (id) => (after.objects[id] ? projectObject(after.objects[id], viewer, after) : null);
        const touched = new Set([...Object.keys(before.objects), ...Object.keys(after.objects)]);
        const gone = [...touched].filter((id) => saw(id) && !sees(id));
        const came = [...touched].filter((id) => !saw(id) && sees(id)).map((id) => sees(id)).filter((p) => p !== null && !('hidden' in p));
        if (body.kind === 'object.duplicate') {
            // The source may have been renamed too: that is a change they see if they see it.
            const src = after.objects[body.id];
            const source = src ? projectObject(src, viewer, after) : null;
            const renamed = source !== null && before.objects[body.id]?.name !== src?.name;
            if (came.length === 0)
                return renamed ? { kind: 'object.change', object: source } : null;
            return { kind: 'objects.appear', objects: renamed ? [source, ...came] : came };
        }
        if (body.kind === 'objects.paste') {
            // Sources renamed by the numbering ("Goblin" became "Goblin 1"): changes they see, if they see them.
            const renamed = [...new Set(body.items.map((i) => i.source))].filter((id) => before.objects[id] && after.objects[id] && before.objects[id].name !== after.objects[id].name)
                .map((id) => projectObject(after.objects[id], viewer, after)).filter((p) => p !== null);
            if (!came.length && !renamed.length)
                return null;
            return came.length ? { kind: 'objects.appear', objects: [...renamed, ...came] } : { kind: 'objects.change', objects: renamed };
        }
        // One thing revealed or hidden is one thing: the singular shape, as a single reveal always was.
        const single = (body.kind === 'object.reveal' || body.kind === 'object.hide') && body.ids.length === 1;
        if (gone.length)
            return single && gone.length === 1 ? { kind: 'object.vanish', id: gone[0] } : { kind: 'objects.vanish', ids: gone };
        if (came.length)
            return single && came.length === 1 ? { kind: 'object.appear', object: came[0] } : { kind: 'objects.appear', objects: came };
        return null;
    }
    const id = body.kind === 'object.add' ? body.object.id : body.id;
    const was = before.objects[id];
    const now = after.objects[id];
    const sawBefore = was ? projectObject(was, viewer, before) : null;
    const seesNow = now ? projectObject(now, viewer, after) : null;
    if (!sawBefore && !seesNow)
        return null;
    if (!sawBefore && seesNow)
        return { kind: 'object.appear', object: seesNow };
    if (sawBefore && !seesNow)
        return { kind: 'object.vanish', id };
    // Nothing this viewer can see changed — a move of a thing the DM is placing (issue 811) — so nothing is sent.
    if (JSON.stringify(sawBefore) === JSON.stringify(seesNow))
        return null;
    const change = { kind: 'object.change', object: seesNow };
    if (body.kind === 'object.move')
        change.route = sightRoute(body.route, now, viewer, after);
    return change;
}
/**
 * What came into this player's sight between two states, whole, and the ids of what left it — for a change that moves
 * no object but moves what is covered (a fog event, the grid under the fog; issue 851). Empty fields are absent.
 */
function sightDelta(before, after, viewer) {
    const appear = [];
    const vanish = [];
    for (const id of new Set([...Object.keys(before.objects), ...Object.keys(after.objects)])) {
        const saw = before.objects[id] ? projectObject(before.objects[id], viewer, before) : null;
        const sees = after.objects[id] ? projectObject(after.objects[id], viewer, after) : null;
        if (saw && !sees)
            vanish.push(id);
        else if (!saw && sees)
            appear.push(sees);
    }
    return { ...(appear.length ? { appear } : {}), ...(vanish.length ? { vanish } : {}) };
}
/**
 * The route a player is shown of a thing they see before and after a move: under the fog (issue 851), only the points
 * in squares they can see — a monster's path through covered squares is as absent as the monster was there. A thing
 * fog never hides (theirs, a player character) keeps its whole route; so does everything with the fog off. Under each
 * character's own sight another player's character follows sight like anything else (§10 Q1, decided B on #1143).
 */
function sightRoute(route, o, viewer, scope) {
    const all = route.map((p) => ({ x: p.x, y: p.y }));
    const pcSeen = o.pc && !(visionOn(scope) && scope.vision.sight === 'character');
    if ((!scope.fog.on && !visionOn(scope)) || pcSeen || o.owners.includes(viewer.id))
        return all;
    // With vision on (issue 1144), only the points in squares their sight reaches: the same rule as the thing itself.
    return all.filter((p) => canSee({ hidden: false, x: p.x, y: p.y, size: o.size, owners: o.owners, pc: o.pc }, viewer, scope));
}
/** Whether a change touched the doors a player is sent with vision off (qa-runs#1202); a scene with no door is never read. */
function doorsChanged(before, after, viewer) {
    const door = (w) => w.door !== undefined;
    if (!(before.walls ?? []).some(door) && !(after.walls ?? []).some(door))
        return false;
    return JSON.stringify(playerWalls(before, viewer.id)) !== JSON.stringify(playerWalls(after, viewer.id));
}
/**
 * With the scene's vision on (issue 1144; design/30 §2.3 step 4): what a change did to this player's sight rides on
 * the frame — their fog (explored and seen), their walls, and whatever came into sight (whole) or left it (ids) that the
 * frame does not already say — so a thing coming into sight arrives whole in one frame. A change that leaves all of
 * it as it was adds nothing, and an event that projected to nothing stays nothing.
 */
function withSight(ev, before, after, viewer) {
    const fogB = playerFogOf(before, viewer);
    const fogA = playerFogOf(after, viewer);
    const wallsB = playerWalls(before, viewer.id);
    const wallsA = playerWalls(after, viewer.id);
    const fogChanged = JSON.stringify(fogB) !== JSON.stringify(fogA);
    const wallsChanged = JSON.stringify(wallsB) !== JSON.stringify(wallsA);
    const lightsB = playerLights(before, viewer.id);
    const lightsA = playerLights(after, viewer.id);
    const lightsChanged = JSON.stringify(lightsB) !== JSON.stringify(lightsA);
    const weatherB = playerWeather(before);
    const weatherA = playerWeather(after);
    const weatherChanged = JSON.stringify(weatherB) !== JSON.stringify(weatherA);
    const told = new Set();
    if (ev && (ev.kind === 'object.appear' || ev.kind === 'object.change'))
        told.add(ev.object.id);
    if (ev && (ev.kind === 'objects.appear' || ev.kind === 'objects.change'))
        for (const o of ev.objects)
            told.add(o.id);
    if (ev && ev.kind === 'object.vanish')
        told.add(ev.id);
    if (ev && ev.kind === 'objects.vanish')
        for (const id of ev.ids)
            told.add(id);
    if (ev && 'appear' in ev && ev.appear)
        for (const o of ev.appear)
            told.add(o.id);
    if (ev && 'vanish' in ev && ev.vanish)
        for (const id of ev.vanish)
            told.add(id);
    // With vision off this runs only for the doors (qa-runs#1202): what a thing's visibility does is the event's own.
    const delta = visionOn(before) || visionOn(after) ? sightDelta(before, after, viewer) : {};
    const appear = (delta.appear ?? []).filter((o) => !told.has(o.id));
    const vanish = (delta.vanish ?? []).filter((id) => !told.has(id));
    if (!fogChanged && !wallsChanged && !lightsChanged && !weatherChanged && !appear.length && !vanish.length)
        return ev;
    const extra = {
        ...(wallsChanged ? { walls: wallsA } : {}),
        ...(lightsChanged ? { lights: lightsA } : {}),
        ...(weatherChanged ? { weather: weatherA } : {}),
        ...(appear.length ? { appear } : {}),
        ...(vanish.length ? { vanish } : {}),
    };
    const merge = (e) => ({
        ...e, ...extra, fog: fogA,
        ...(appear.length || e.appear ? { appear: [...(e.appear ?? []), ...appear] } : {}),
        ...(vanish.length || e.vanish ? { vanish: [...(e.vanish ?? []), ...vanish] } : {}),
    });
    if (!ev)
        return { kind: 'fog.change', fog: fogA, ...extra };
    const changed = (e) => merge({ kind: 'objects.change', ...e });
    switch (ev.kind) {
        case 'fog.change':
        case 'grid.change':
        case 'objects.change': return merge(ev);
        case 'object.change': return changed({ objects: [ev.object], ...(ev.route ? { routes: { [ev.object.id]: ev.route } } : {}) });
        case 'object.appear': return changed({ objects: [], appear: [ev.object] });
        case 'objects.appear': return changed({ objects: [], appear: ev.objects });
        case 'object.vanish': return changed({ objects: [], vanish: [ev.id] });
        case 'objects.vanish': return changed({ objects: [], vanish: ev.ids });
        // Nothing else moves a thing or a wall; were it ever to, the fields ride along and the browser applies them.
        default: return { ...ev, ...extra, fog: fogA };
    }
}
/**
 * Apply a projected event to a projected state — what the browser runs. Any event may carry `appear` (things that came
 * into this viewer's sight, whole), `vanish` (ids that left it) and `encounter` (the turn order as now read); they are
 * applied after the event's own change, whatever its kind.
 */
export function applyProjected(state, ev) {
    if (ev.kind === 'roll' || ev.kind === 'log.clear' || ev.kind === 'log.note' || ev.kind === 'ask.set' || ev.kind === 'ask.clear' || ev.kind === 'rulings' || ev.kind === 'handout.set' || ev.kind === 'handout.clear' || ev.kind === 'audio' || ev.kind === 'dialog' || ev.kind === 'dialog.gone' || ev.kind === 'deck' || ev.kind === 'deck.gone' || ev.kind === 'card.revealed')
        return state; // the log is held beside the state, not in it
    // A rewind (issue 1169) is the restored scene as this viewer may see it, whole: it replaces what they held.
    if (ev.kind === 'table.rewind' || ev.kind === 'table.floors')
        return ev.state;
    if (ev.kind === 'dm.event')
        return state; // the DM's client reduces the body with applyEvent over its full state
    let objects = state.objects;
    const touch = () => (objects === state.objects ? (objects = { ...state.objects }) : objects);
    let next = state;
    switch (ev.kind) {
        case 'object.appear':
        case 'object.change':
            touch()[ev.object.id] = ev.object;
            break;
        case 'object.vanish':
            delete touch()[ev.id];
            break;
        case 'objects.appear':
        case 'objects.change':
            for (const o of ev.objects)
                touch()[o.id] = o;
            break;
        case 'objects.vanish':
            for (const id of ev.ids)
                delete touch()[id];
            break;
        case 'grid.change':
            next = { ...next, grid: ev.grid, ...(ev.fog ? { fog: ev.fog } : {}) };
            break;
        case 'fog.change':
            next = { ...next, fog: ev.fog };
            break;
        case 'scene.change':
            next = { ...next, scene: { ...ev.scene } };
            break;
        case 'encounter.change':
            next = { ...next, encounter: ev.encounter };
            break;
        case 'chase.set':
            if (ev.chase)
                next = { ...next, chase: ev.chase };
            else {
                const { chase: _c, ...rest } = next;
                next = rest;
            }
            break;
        case 'focus.set':
            next = { ...next, focuses: { ...state.focuses, [ev.focus.id]: ev.focus } };
            break;
        case 'focus.clear': {
            const focuses = { ...state.focuses };
            for (const id of ev.ids)
                delete focuses[id];
            next = { ...next, focuses };
            break;
        }
        case 'area.set':
            next = { ...next, areas: { ...(state.areas ?? {}), [ev.area.id]: ev.area } };
            break;
        case 'area.clear': {
            const areas = { ...(state.areas ?? {}) };
            for (const id of ev.ids)
                delete areas[id];
            next = { ...next, areas };
            break;
        }
        case 'draw.add':
            next = { ...next, drawings: [...(state.drawings ?? []).filter((d) => d.id !== ev.drawing.id), ev.drawing] };
            break;
        case 'draw.addMany': {
            const fresh = new Set(ev.drawings.map((d) => d.id));
            next = { ...next, drawings: [...(state.drawings ?? []).filter((d) => !fresh.has(d.id)), ...ev.drawings] };
            break;
        }
        case 'draw.erase': {
            const gone = new Set(ev.ids);
            next = { ...next, drawings: (state.drawings ?? []).filter((d) => !gone.has(d.id)) };
            break;
        }
        // Scene links (issue 1173): the shown ones of this scene, whole; none, and the key goes.
        case 'toggles':
            if (Object.keys(ev.toggles).length)
                next = { ...next, toggles: ev.toggles };
            else {
                const { toggles: _t, ...rest } = next;
                next = rest;
            }
            break;
        case 'links':
            if (ev.links.length)
                next = { ...next, links: linkRecord(ev.links) };
            else {
                const { links: _l, ...rest } = next;
                next = rest;
            }
            break;
    }
    if ('appear' in ev && ev.appear)
        for (const o of ev.appear)
            touch()[o.id] = o;
    if ('vanish' in ev && ev.vanish)
        for (const id of ev.vanish)
            delete touch()[id];
    if ('encounter' in ev && ev.encounter)
        next = { ...next, encounter: ev.encounter };
    // Sight (issue 1144): any frame may carry this player's fog and walls; null walls means vision went off, and the key goes.
    if ('fog' in ev && ev.fog && ev.kind !== 'grid.change' && ev.kind !== 'fog.change')
        next = { ...next, fog: ev.fog };
    if ('walls' in ev && ev.walls !== undefined) {
        if (ev.walls === null) {
            const { walls: _w, ...rest } = next;
            next = rest;
        }
        else
            next = { ...next, walls: ev.walls };
    }
    // Light (issue 1145): the lights whose source they see; null means none is theirs to draw now, and the key goes.
    if ('lights' in ev && ev.lights !== undefined) {
        if (ev.lights === null) {
            const { lights: _l, ...rest } = next;
            next = rest;
        }
        else
            next = { ...next, lights: ev.lights };
    }
    // Weather (issue 1163): its name while vision is on; null means none is theirs now, and the key goes.
    if ('weather' in ev && ev.weather !== undefined) {
        if (ev.weather === null) {
            const { weather: _w, ...rest } = next;
            next = rest;
        }
        else
            next = { ...next, weather: { ...ev.weather } };
    }
    // The chase (qa-runs#1174), riding on another change: their copy now; null, none, and the key goes.
    if (ev.kind !== 'chase.set' && ev.chase !== undefined) {
        if (ev.chase)
            next = { ...next, chase: ev.chase };
        else {
            const { chase: _c, ...rest } = next;
            next = rest;
        }
    }
    // Movement paused (qa-runs#1426): their held tokens now; none, and the key goes.
    if (ev.paused !== undefined) {
        if (ev.paused.length)
            next = { ...next, paused: [...ev.paused] };
        else {
            const { paused: _p, ...rest } = next;
            next = rest;
        }
    }
    // Pins (issue 1154): the whole list they may see now; none, and the key goes.
    if (ev.pins) {
        if (ev.pins.length)
            next = { ...next, pins: pinRecord(ev.pins) };
        else {
            const { pins: _p, ...rest } = next;
            next = rest;
        }
    }
    if ('drawings' in ev && ev.drawings) {
        const by = new Map(ev.drawings.map((d) => [d.id, d]));
        next = { ...next, drawings: (next.drawings ?? []).map((d) => by.get(d.id) ?? d) };
    }
    return objects === state.objects ? next : { ...next, objects };
}
/** Where a change leaves an object, for the client's animation along a route. */
export const routeEnd = destinationOf;
/** A plain roll as a player reads it (qa-runs#406): no fudge band or fudge, and no application to a token the DM had hidden. */
export function playerRollCopy(r) {
    const { band: _band, applied, ...seen } = r;
    const shown = applied?.filter((a) => !a.hidden).map(playerApplied);
    return { ...seen, dice: [...r.dice], fudged: null, ...(shown?.length ? { applied: shown } : {}) };
}
/** A line of NPC dialog, or its deletion, as one player gets it: nothing unless they heard it (qa-runs#1167). */
function dialogFrame(body, viewerId, at) {
    if (body.kind === 'dialog.delete')
        return body.heard.includes(viewerId) ? { kind: 'dialog.gone', id: body.id } : null;
    const line = playerDialogLine(body.line, viewerId, at);
    return line ? { kind: 'dialog', line } : null;
}
//# sourceMappingURL=project.js.map